import { useParams, Link, Navigate } from 'react-router-dom';

interface GlossaryTerm {
    term: string;
    definition: string;
}

interface GameGuideContent {
    id: string;
    title: string;
    platform: string;
    esrb: string;
    esrbDescription: string;
    heroImage: string;
    whatIsThisGame: string[];
    explainerVideos: { title: string; embedUrl: string; description: string; }[];
    rules: {
        format: string;
        matchLength: string;
        howToWin: string[];
    };
    scoring: {
        explanation: string[];
        keyTerms: GlossaryTerm[];
    };
    whatToWatch: {
        excitingMoments: string[];
        glossary: GlossaryTerm[];
    };
    supportingYourPlayer: string[];
    enjoyingWatching: string[];
}

const guides: Record<string, GameGuideContent> = {
    'smash-bros': {
        id: 'smash-bros',
        title: 'Super Smash Bros. Ultimate',
        platform: 'Nintendo Switch',
        esrb: 'E10+',
        esrbDescription: 'Cartoon Violence, Comic Mischief',
        heroImage: '/images/games/smash-bros.png',
        whatIsThisGame: [
            "Super Smash Bros. Ultimate is a fighting game where iconic video game characters (like Mario, Pikachu, and Link) battle to knock each other off the stage. Think of it like sumo wrestling but with colorful Nintendo characters!",
            "Unlike traditional fighting games where you reduce a health bar, in Smash Bros. the goal is to hit your opponent until they fly off the screen. The more you hit someone, the farther they fly when hit again.",
            "It's fast-paced, colorful, and has absolutely no blood or gore—defeated characters simply fly off screen with a cartoonish 'star KO' effect. It's suitable for all ages.",
        ],
        explainerVideos: [
            {
                title: "Smash Bros. Beginner's Guide - Basics & Terminology",
                embedUrl: "https://www.youtube.com/embed/ta3L35wsE6o",
                description: "A beginner-friendly guide covering fundamental mechanics like percentage, movement options, attack types, and shielding.",
            },
            {
                title: "5 Fundamentals for Beginners",
                embedUrl: "https://www.youtube.com/embed/DrBWKkO9OeU",
                description: "Covers jump types, aerial drift, directional influence (DI), and how to counter defensive opponents.",
            },
        ],
        rules: {
            format: "1v1 (Solos) or 3v3 (Crews)",
            matchLength: "Each game lasts about 3-7 minutes",
            howToWin: [
                "Each player starts with a set number of 'stocks' (lives)—typically 3",
                "Hit your opponent to increase their 'damage percentage' (shown as a number like 85%)",
                "The higher someone's percentage, the farther they fly when hit",
                "Launch them off the screen (any direction) to take one of their stocks",
                "The last player with stocks remaining wins the game",
                "In competitive matches, players often play 'best of 3' or 'best of 5' games",
            ],
        },
        scoring: {
            explanation: [
                "The percentage number you see (0% to 999%) represents how much damage a character has taken. It's NOT health—higher is worse!",
                "At 0%, hits barely move you. At 100%+, a strong attack could launch you off the screen.",
                "There's no score in the traditional sense—it's simply about taking all your opponent's stocks before they take yours.",
            ],
            keyTerms: [
                { term: "Stock", definition: "A life. Lose all your stocks and you lose the game." },
                { term: "Percentage (%)", definition: "Damage taken. Higher = you fly farther when hit. Resets to 0% when you lose a stock." },
                { term: "KO (Knockout)", definition: "When a player is launched off the screen, losing a stock." },
                { term: "SD (Self-Destruct)", definition: "When a player accidentally falls off the stage on their own—very embarrassing!" },
            ],
        },
        whatToWatch: {
            excitingMoments: [
                "🔥 **Comebacks**: When a player is down stocks but battles back to win—the crowd goes wild!",
                "🎯 **Edge Guards**: When a player goes offstage to attack their recovering opponent—high risk, high reward!",
                "⚡ **Combos**: Stringing multiple hits together without the opponent being able to escape",
                "💀 **Spike KOs**: Hitting someone downward offstage for an early KO—very hype!",
                "🛡️ **Clutch Shields**: Blocking a powerful attack at just the right moment",
                "🎭 **Character Matchups**: Some characters naturally do better against others—like rock-paper-scissors",
            ],
            glossary: [
                { term: "Neutral", definition: "When both players are at center stage, feeling each other out before committing to attacks" },
                { term: "Offstage", definition: "The dangerous area beyond the stage edges—fall too far and you lose a stock" },
                { term: "Recovery", definition: "Using jumps and special moves to get back to the stage after being knocked off" },
                { term: "Edge/Ledge", definition: "The edge of the stage that players grab to climb back up" },
                { term: "Punish", definition: "Attacking your opponent after they miss an attack or make a mistake" },
                { term: "Read", definition: "Predicting what your opponent will do and preparing the perfect counter—very skillful!" },
                { term: "Mix-up", definition: "Varying your options to be unpredictable and keep opponents guessing" },
                { term: "Frame", definition: "1/60th of a second. Smash runs at 60 frames per second. You might hear 'that move is 7 frames'—higher level play!" },
                { term: "Main", definition: "A player's primary character that they're best at" },
                { term: "Secondary", definition: "A backup character for bad matchups" },
            ],
        },
        supportingYourPlayer: [
            "**Encourage practice time** — Improvement comes from repetition. The game's Training Mode is excellent for learning.",
            "**Ask about their 'main'** — Their favorite character! Learn a bit about that character to connect with them.",
            "**Watch VODs together** — 'VOD' means Video On Demand (recorded matches). Reviewing past games helps players improve.",
            "**Celebrate growth, not just wins** — Did they pull off a new combo? That's worth celebrating even if they lost!",
            "**Stay positive after losses** — Competitive gaming has many losses. Help them focus on what they learned.",
            "**Respect their practice time** — Just like sports practice, dedicated gaming practice is valuable.",
            "**Ask questions!** — Players love explaining what they're doing. It builds confidence and communication skills.",
        ],
        enjoyingWatching: [
            "**Watch the percentages** — When someone hits 100%+, a big launch could happen any moment!",
            "**Count the stocks** — The stock icons show how many lives each player has left",
            "**Cheer for recoveries** — When your player is knocked offstage, root for them to make it back!",
            "**Notice the character matchup** — Ask your player beforehand if they think it's a good or bad matchup",
            "**Listen to commentary** — If streams have commentators, they explain what's happening",
            "**Don't worry if it's fast** — Even experienced spectators can't track everything. Focus on the stocks and big moments.",
            "**The crowd knows** — If you're at a live event, the crowd reaction tells you when something big happens!",
            "**It's okay to ask** — After games, ask your player 'what happened there?' They'll be happy to explain.",
        ],
    },
    'mario-kart': {
        id: 'mario-kart',
        title: 'Mario Kart 8 Deluxe',
        platform: 'Nintendo Switch',
        esrb: 'E',
        esrbDescription: 'Comic Mischief',
        heroImage: '/images/games/mario-kart.png',
        whatIsThisGame: [
            "Mario Kart is a colorful racing game featuring Nintendo characters in go-karts. Think of it like a family-friendly version of NASCAR, but with banana peels and turtle shells as obstacles!",
            "Players race around themed tracks, collecting power-up items from rainbow-colored boxes to help themselves or hinder opponents. Items include speed-boosting mushrooms, protective shells, and even the dreaded Blue Shell that targets the leader!",
            "It's incredibly pick up and play—the game even has 'Smart Steering' and 'Auto-Accelerate' modes to help newer players stay on track. But at the competitive level, mastering drifting, item management, and track shortcuts provides surprising strategic depth.",
            "This is one of the most family-friendly and accessible esports games. Many parents find they can jump right in and race alongside their children!",
        ],
        explainerVideos: [
            {
                title: "Mario Kart 8 Deluxe - Beginner's Guide",
                embedUrl: "https://www.youtube.com/embed/L5wuMhNnuGo",
                description: "Covers the basics of racing, items, drifting, and the game's assist features for new players.",
            },
            {
                title: "Mario Kart Tips & Tricks",
                embedUrl: "https://www.youtube.com/embed/7M-0mIJb_AQ",
                description: "Learn advanced techniques like start boosts, optimal drifting, and coin collection strategies.",
            },
        ],
        rules: {
            format: "2v2v2v2 (Teams of 2 per school, 4 schools compete)",
            matchLength: "3 heats of 4 races each, about 45 minutes total",
            howToWin: [
                "Teams of 2 players compete together—their individual race placements combine for a team score",
                "Points are awarded based on finishing position: 1st = 15 points, 2nd = 12, 3rd = 10, down to 12th = 1 point",
                "After 4 races (one 'heat'), points are tallied and compared between schools",
                "The school-team with the highest combined points across all heats wins the match",
                "In close matches, individual race wins can be tiebreakers",
            ],
        },
        scoring: {
            explanation: [
                "Points are awarded based on finishing position in each race—1st place gets 15 points, scaling down to 1 point for 12th place.",
                "Team scores are the combined points of both players from that school. So if your players finish 2nd (12 pts) and 5th (8 pts), your team earns 20 points for that race.",
                "Consistency matters! It's often better to have two 4th-place finishes than one 1st and one 10th.",
            ],
            keyTerms: [
                { term: "Heat", definition: "A set of 4 consecutive races. Matches typically have 3 heats." },
                { term: "Item Box", definition: "Rainbow-colored boxes on the track that give random power-up items when driven through." },
                { term: "Drift/Mini-Turbo", definition: "Holding the turn button while cornering builds up sparks. Releasing gives a speed boost!" },
                { term: "Slipstream", definition: "Driving directly behind another racer for a few seconds gives you a speed boost." },
                { term: "Coins", definition: "Collecting coins (up to 10) increases your kart's top speed slightly." },
                { term: "Start Boost", definition: "Pressing accelerate at the perfect moment during countdown gives a speed boost off the line." },
            ],
        },
        whatToWatch: {
            excitingMoments: [
                "🍌 **Item Chaos on Final Lap**: The last lap often features a flurry of items that can completely shuffle positions",
                "🏁 **Photo Finishes**: Races can be decided by fractions of a second—watch for the slow-motion replay!",
                "💙 **Blue Shell Drama**: The Blue Shell targets whoever is in 1st place and can devastate a race leader right before the finish",
                "🍄 **Shortcut Mushrooms**: Using boost items to cut through grass or skip sections of track",
                "⭐ **Star Power**: When a racer gets a Star, they're invincible and super fast—watch them tear through the pack!",
                "🔄 **Last-to-First Comebacks**: Sometimes a well-timed item can take someone from the back to the front",
            ],
            glossary: [
                { term: "Blue Shell", definition: "A powerful homing item that targets the 1st place racer, causing a massive explosion. Very dramatic!" },
                { term: "Red Shell", definition: "A homing shell that chases the racer directly ahead—can be blocked with items held behind you" },
                { term: "Green Shell", definition: "A shell that travels in a straight line—requires skill to aim" },
                { term: "Banana", definition: "A slippery peel you drop behind you—causes spinouts" },
                { term: "Mushroom", definition: "A boost item that gives you a burst of speed" },
                { term: "Star", definition: "Makes you invincible and faster for a short time—can knock other racers out of the way" },
                { term: "Lightning", definition: "Shrinks ALL other racers, making them slower (a rare and powerful item)" },
                { term: "Bullet Bill", definition: "Temporarily transforms your kart into an auto-piloting bullet that blasts through the track" },
            ],
        },
        supportingYourPlayer: [
            "**Play together!** — Mario Kart is one of the most accessible games for parents to enjoy with their kids",
            "**Ask about their favorite tracks** — Players develop preferences and strategies for specific courses",
            "**Understand item luck** — Sometimes bad items at the wrong time cause losses. It's part of the game!",
            "**Controller practice helps** — Timing drifts and holding items behind as shields takes muscle memory",
            "**Discuss team strategy** — Partners sometimes coordinate who leads vs. who plays defensively",
            "**Celebrate good races** — Even if the team loses, individual strong performances deserve recognition",
            "**Watch professional Mario Kart** — There's a surprisingly active competitive scene with advanced techniques",
        ],
        enjoyingWatching: [
            "**Watch the minimap** — The bottom corner shows all racer positions on the track in real-time",
            "**Count the laps** — Most races are 3 laps; the final lap music speeds up to build tension!",
            "**Look for the positions** — Each racer's current place (1st, 2nd, etc.) is shown on screen",
            "**Cheer during item chaos** — The last lap often has the most action as items fly everywhere",
            "**Notice the item someone is holding** — Especially if they have a Blue Shell... that's about to get exciting!",
            "**Team colors help** — Our players may have matching kart colors to help you track them",
            "**Photo finishes are common** — Races can come down to milliseconds, so watch until the very end!",
        ],
    },
    'rocket-league': {
        id: 'rocket-league',
        title: 'Rocket League',
        platform: 'PC',
        esrb: 'E',
        esrbDescription: 'Mild Fantasy Violence',
        heroImage: '/images/games/rocket-league.png',
        whatIsThisGame: [
            "Rocket League is 'soccer with rocket-powered cars.' Really, that's it! Cars drive around an enclosed arena trying to hit a giant ball into the opponent's goal.",
            "The 'mild fantasy violence' rating comes from cars being able to 'demolish' (explode) each other by ramming at high speed, but they respawn instantly (about 3 seconds) and there's no gore whatsoever—just a quick explosion effect.",
            "What makes the game special is that cars can fly! By using rocket boost, players can launch into the air to make aerial plays. Mastering flight is one of the most impressive skills in all of esports.",
            "It's incredibly fast-paced and one of the most visually exciting esports to watch. The rules are simple (score goals!), so even newcomers can follow the action immediately.",
        ],
        explainerVideos: [
            {
                title: "Rocket League Explained for Parents",
                embedUrl: "https://www.youtube.com/embed/tMKB5Pf8v2g",
                description: "A comprehensive parent's guide covering gameplay, safety features, parental controls, and what to expect.",
            },
            {
                title: "Rocket League Beginner Tutorial",
                embedUrl: "https://www.youtube.com/embed/wZA6xL7mXIU",
                description: "Covers the fundamentals of driving, boosting, jumping, and basic ball control for new players.",
            },
        ],
        rules: {
            format: "2v2 (Team of 4 players, rotating between games)",
            matchLength: "5 minutes per game (plus overtime if tied)",
            howToWin: [
                "Score more goals than the opposing team within the 5-minute time limit",
                "If the score is tied at the end, sudden-death overtime begins—first goal wins!",
                "Overtime continues indefinitely until someone scores (the ball needs to touch the ground first if it goes to overtime)",
                "Matches are typically best-of-5 or best-of-7 games between schools",
                "Teams can substitute players between games",
            ],
        },
        scoring: {
            explanation: [
                "It's just like soccer! Each goal counts as 1 point. The team with the most goals at the end wins.",
                "The ball must completely cross the goal line to count. Sometimes this leads to dramatic 'goal-line' saves!",
                "Players earn individual in-game points for various actions (goals, assists, saves, shots), but only actual goals count toward winning.",
            ],
            keyTerms: [
                { term: "Boost", definition: "Rocket fuel that lets cars go faster and fly. Collected from orange pads (12 boost) and corner orbs (100 boost) around the arena." },
                { term: "Demo/Demolition", definition: "Destroying an opponent's car by ramming them at supersonic speed. They respawn in 3 seconds." },
                { term: "Supersonic", definition: "Maximum driving speed, shown by purple trails from wheels. Required for demos." },
                { term: "Aerial", definition: "Using boost to fly through the air and hit the ball—an advanced technique!" },
                { term: "Double Jump", definition: "Players can jump twice before their wheels touch the ground again." },
                { term: "Flip/Dodge", definition: "A quick directional move that gives speed and can hit the ball harder." },
                { term: "Kickoff", definition: "How each goal starts—both teams race for the ball at center field." },
            ],
        },
        whatToWatch: {
            excitingMoments: [
                "✈️ **Aerial Goals**: Players flying high through the air to hit the ball into the goal—the skill ceiling is incredible!",
                "🧱 **Epic Saves**: Last-second blocks on the goal line, sometimes requiring aerials or desperate lunges",
                "💥 **Demolition Plays**: Strategic destruction of opponents to create open goals",
                "🎯 **Redirect Goals**: One player passes the ball, and their teammate taps it mid-air into the goal",
                "⏰ **Overtime Winners**: Sudden-death goals after a tense, extended overtime period",
                "🔄 **Zero-Second Goals**: Scoring after the timer hits 0:00 (the game continues until the ball touches the ground!)",
                "🏃 **Solo Plays**: One player dribbling the ball on their car's roof and outplaying multiple defenders",
            ],
            glossary: [
                { term: "Rotation", definition: "How teammates cycle between attacking, midfield, and goalkeeping positions—essential teamwork!" },
                { term: "Passing Play", definition: "Teammates working together to pass the ball and create scoring opportunities" },
                { term: "Dribble", definition: "Keeping the ball balanced on top of your car while driving—very skillful!" },
                { term: "Pinch", definition: "Two cars hitting the ball simultaneously from opposite sides, sending it flying at extreme speed" },
                { term: "50/50", definition: "When two players hit the ball at the same time, resulting in an unpredictable bounce" },
                { term: "Center/Cross", definition: "Hitting the ball from the side toward the front of the goal for a teammate to score" },
                { term: "Clear", definition: "Hitting the ball away from your own goal to prevent a scoring chance" },
                { term: "Ball Cam", definition: "Camera mode that always focuses on the ball—most players use this almost exclusively" },
                { term: "Whiff", definition: "Missing the ball completely when trying to hit it—happens to everyone!" },
            ],
        },
        supportingYourPlayer: [
            "**It's like sports!** — Your existing sports knowledge applies directly: passing, positioning, teamwork, rotation",
            "**Aerials take many hours** — Flying and hitting the ball is an advanced skill. Celebrate progress, not perfection!",
            "**Free play practice is valuable** — Time spent just driving and hitting the ball in practice mode builds fundamentals",
            "**Encourage team communication** — Partners need to call out their positions and intentions",
            "**Rotation takes time to learn** — Knowing when to attack vs. defend is a skill that develops over months",
            "**Whiffs happen!** — Even professionals miss the ball. It's a game with a very high skill ceiling.",
            "**Watch pro matches together** — RLCS (Rocket League Championship Series) shows what's possible and is exciting to watch",
        ],
        enjoyingWatching: [
            "**Watch it like soccer** — Goal scoring, saves, and passing are all familiar concepts",
            "**The boost meter matters** — Players without boost (shown by their car's trail) are at a disadvantage",
            "**Watch for aerial plays** — When a player flies, something exciting is about to happen!",
            "**Overtime is thrilling** — Sudden death means every touch could end the game",
            "**Ball never stops** — Until a goal is scored or the ball touches the ground with 0:00 on clock, the play continues",
            "**The replay shows detail** — After goals, the replay shows what happened from multiple angles",
            "**Numbers count** — If one team has more players near the ball (after a demo), they have the advantage",
        ],
    },
    'fortnite': {
        id: 'fortnite',
        title: 'Fortnite',
        platform: 'PC',
        esrb: 'T',
        esrbDescription: 'Action, Violence (Cartoon)',
        heroImage: '/images/games/fortnite.png',
        whatIsThisGame: [
            "Fortnite is a 'battle royale' game where teams compete to be the last ones standing. Imagine a high-stakes game of hide-and-seek combined with laser tag, played in a colorful, shrinking arena.",
            "The violence is entirely cartoonish—eliminated players simply poof away with a colorful effect. There's no blood, gore, or realistic violence. Characters use weapons, but the style is very arcade-like.",
            "What makes Fortnite unique is 'building'—players can instantly construct walls, ramps, and floors for defense and positioning. This adds incredible strategic depth and is what separates skilled players from beginners.",
            "For our esports program, we use school-managed accounts with voice chat disabled for communication with opponents. Players communicate with teammates through our Discord server with coach supervision.",
        ],
        explainerVideos: [
            {
                title: "Fortnite for Parents - A Dad's Guide",
                embedUrl: "https://www.youtube.com/embed/80VmiLDQ1PI",
                description: "A parent-friendly explanation of what Fortnite is, how it works, and what to know about your child playing it.",
            },
            {
                title: "Fortnite Beginner's Guide 2024",
                embedUrl: "https://www.youtube.com/embed/EIJ_ITHX3Zw",
                description: "Covers the basics of looting, building, combat, and surviving in Fortnite for new players.",
            },
        ],
        rules: {
            format: "Duos (2 players work together as partners)",
            matchLength: "15-25 minutes per match, depending on performance",
            howToWin: [
                "Players drop onto an island from a flying bus and must find weapons and supplies",
                "The play area (called 'the storm') shrinks over time, forcing players into smaller spaces",
                "Eliminate enemy teams while surviving the shrinking safe zone",
                "The last team with players alive wins the match (a 'Victory Royale')",
                "In competitive scoring, placement and elimination points both count toward the total",
            ],
        },
        scoring: {
            explanation: [
                "Competitive Fortnite uses a point system: you earn points based on how long you survive (placement) AND how many eliminations your team gets.",
                "High placements (like top 10) earn significant points. A Victory Royale earns the most placement points.",
                "Eliminations earn 1-2 points each, encouraging aggressive play while still rewarding survival.",
                "Over multiple matches, consistent performance adds up. Teams aim for the highest total points.",
            ],
            keyTerms: [
                { term: "Storm/Circle", definition: "The shrinking safe zone. Areas outside take damage. The storm pushes everyone toward the center." },
                { term: "Mats/Materials", definition: "Building resources: Wood, Brick, and Metal. Collected by breaking objects or finding them." },
                { term: "Loot", definition: "Weapons, healing items, and gear found in buildings and chests around the map." },
                { term: "Drop", definition: "Where you choose to land from the battle bus at the start of the match." },
                { term: "Knocked", definition: "In duos, when your health hits zero you're 'knocked' and can be revived by your partner—if they reach you in time!" },
                { term: "Eliminated", definition: "Fully removed from the match (both teammates knocked or finished off)." },
                { term: "Victory Royale", definition: "The win! Being the last team standing." },
            ],
        },
        whatToWatch: {
            excitingMoments: [
                "🏗️ **Build Battles**: Players rapidly building structures to gain height advantage—looks like instant architecture!",
                "🎯 **Clutch Eliminations**: Taking down opponents in intense 1v1 situations, especially when outnumbered",
                "💚 **Revive Saves**: Rescuing a knocked teammate under fire—very tense!",
                "🏆 **Victory Royale**: Winning the entire match—the most satisfying moment in Fortnite",
                "🌀 **Storm Rotations**: Racing to get inside the safe zone while fighting other teams doing the same",
                "📦 **Supply Drop Fights**: Multiple teams fighting over rare loot drops—high risk, high reward",
                "🎒 **Loadout Decisions**: Watching players choose which weapons and items to carry (only 5 slots!)",
            ],
            glossary: [
                { term: "High Ground", definition: "Being above your opponent is a huge advantage for seeing and shooting them" },
                { term: "Box Fight", definition: "Fighting in small built structures, a cramped 1v1 situation requiring quick edits and shots" },
                { term: "Edit", definition: "Changing the shape of a built structure (adding doors, windows) for tactical advantage" },
                { term: "Rotation", definition: "Moving to the next safe zone before the storm closes in" },
                { term: "Third Party", definition: "A team attacking while two other teams are already fighting—opportunistic but risky" },
                { term: "Heal Off", definition: "Staying alive in the final storm by having more healing items than opponents" },
                { term: "Loadout", definition: "Your combination of weapons and items—what you choose to carry into battle" },
                { term: "POI (Point of Interest)", definition: "Named locations on the map where loot is plentiful but other players often land" },
                { term: "Zero Build", definition: "A game mode without building—focuses purely on positioning and gunplay" },
            ],
        },
        supportingYourPlayer: [
            "**Communication is essential** — Duos partners need constant callouts about enemy positions, plans, and resources",
            "**Building takes hundreds of hours** — This is the hardest skill for new players. Progress is slow but rewarding!",
            "**Drops are strategic** — Where you land affects your early game. Ask about their favorite drop spots!",
            "**RNG (randomness) affects matches** — Sometimes you find great loot, sometimes you don't. Variance is part of the game.",
            "**VOD review helps a lot** — Recording and reviewing matches helps identify mistakes and improvement areas",
            "**Celebrate high placements** — Even without a Victory Royale, making top 10 or top 5 is an achievement",
            "**Build practice maps exist** — Creative mode maps specifically for practicing building techniques",
        ],
        enjoyingWatching: [
            "**Watch the player count** — The number of remaining players is shown on screen. Lower numbers = more tension!",
            "**Endgame is the most exciting** — The final circles with few teams left are absolutely thrilling",
            "**Building is often overwhelming** — Don't worry if you can't follow every wall and ramp. Focus on the eliminations!",
            "**The storm circle indicator** — Shows where players need to go. Watch for teams caught outside in the storm.",
            "**Listen for callouts** — Players communicate enemy positions. You'll learn terms like 'on me' and 'cracked them'",
            "**The minimap tells a story** — It shows the current and next storm circle locations",
            "**Victory Royale is electric!** — When it happens, it's an unforgettable moment. Celebrate with them!",
        ],
    },
    'madden': {
        id: 'madden',
        title: 'Madden NFL 26',
        platform: "Player's Own Device (BYOD)",
        esrb: 'E',
        esrbDescription: 'Mild Lyrics, Simulated Gambling',
        heroImage: '/images/games/madden.png',
        whatIsThisGame: [
            "Madden is the premier NFL football simulation game. If you know football, you already understand 90% of what's happening! It's real plays, real teams, and real strategies brought to life.",
            "Players control an entire NFL team—calling offensive and defensive plays, making adjustments at the line, and controlling individual players during the action.",
            "The 'simulated gambling' in the rating refers to the 'Ultimate Team' mode where you open packs of virtual cards—like trading cards. Our school program does NOT use this mode, so there are no in-game purchases or gambling mechanics during our competitions.",
            "This is our 'Bring Your Own Device' esport—students play on their own PlayStation or Xbox consoles, making it more accessible to participate.",
        ],
        explainerVideos: [
            {
                title: "Madden Beginner's Guide",
                embedUrl: "https://www.youtube.com/embed/rGiH7A-OIvE",
                description: "Covers the fundamental controls, basic plays, passing techniques, and defensive strategies for new players.",
            },
            {
                title: "How to Get Better at Madden",
                embedUrl: "https://www.youtube.com/embed/3fJgZByqHzc",
                description: "Tips on hot routes, playbook familiarity, and reading defenses—key skills for improving your game.",
            },
        ],
        rules: {
            format: "1v1 with Practice Squad support (teammates help prepare)",
            matchLength: "4 quarters, about 30-45 minutes per game depending on clock settings",
            howToWin: [
                "Score more points than your opponent—just like real football!",
                "Touchdowns = 6 points + extra point attempt (1 point kick, 2 point conversion)",
                "Field Goals = 3 points",
                "Safeties = 2 points (plus you get the ball back!)",
                "Standard NFL rules apply: 4 downs to get 10 yards, turnovers, penalties, etc.",
                "League matches may use specific settings like accelerated clock or quarter length",
            ],
        },
        scoring: {
            explanation: [
                "Exactly like real NFL football! Touchdowns are worth 6 points, with an extra point kick (1) or two-point conversion attempt (2).",
                "Field goals are worth 3 points and are typically attempted on 4th down when you're close but not in touchdown range.",
                "Safeties (tackling the ball carrier in their own end zone) give you 2 points AND possession—rare but exciting!",
            ],
            keyTerms: [
                { term: "Audible", definition: "Changing the play at the line of scrimmage after seeing the defense's formation. Critical for competitive play!" },
                { term: "Hot Route", definition: "Changing an individual receiver's route before the snap to attack specific defensive weaknesses." },
                { term: "User", definition: "The specific player your child manually controls (usually a linebacker or safety on defense, QB/receiver on offense)." },
                { term: "User Pick/User Lurk", definition: "Intercepting a pass by manually controlling a defender—one of the most exciting plays in Madden!" },
                { term: "Nano Blitz", definition: "A defensive play designed to create fast, hard-to-block pressure. Considered 'cheap' by some players." },
                { term: "Money Play", definition: "A reliable play that consistently works well—every player has a few favorites." },
                { term: "Playbook", definition: "The collection of offensive and defensive plays available. Knowing your playbook is half the battle." },
            ],
        },
        whatToWatch: {
            excitingMoments: [
                "🏈 **User Picks/Lurks**: Intercepting a pass by manually controlling a defender—huge momentum swings!",
                "🏃 **Breakaway Runs**: Breaking tackles for big gains or house-call touchdowns",
                "🎯 **Aggressive Catches**: Contested catches where the receiver battles through contact",
                "🧱 **Goal Line Stands**: Defense stopping the offense from scoring at the 1-yard line",
                "⏰ **Two-Minute Drills**: Intense end-of-half or end-of-game drives to score",
                "🔄 **4th Down Conversions**: Going for it on 4th down and converting—high risk, high reward!",
                "💥 **Big Hits**: Spectacular tackles that force fumbles or stop key plays",
            ],
            glossary: [
                { term: "Blitz", definition: "Sending extra defenders to rush the quarterback, hoping for a sack but leaving receivers open" },
                { term: "Clock Management", definition: "Running out the clock when ahead, or preserving time when behind—crucial in close games" },
                { term: "Formation", definition: "How players line up before the snap. Different formations favor running vs. passing." },
                { term: "Cover 2/3/4", definition: "Defensive zone schemes named for how many deep defenders there are" },
                { term: "Man Coverage", definition: "Defense where each defender follows a specific offensive player" },
                { term: "Zone Coverage", definition: "Defense where defenders cover areas of the field rather than specific players" },
                { term: "Scramble", definition: "When the quarterback runs with the ball instead of throwing—sometimes planned, sometimes desperation" },
                { term: "Pick (Screen)", definition: "An offensive play where blockers set up receivers—different from an interception 'pick'" },
            ],
        },
        supportingYourPlayer: [
            "**Your football knowledge applies!** — Unlike other esports, you can directly apply real sports knowledge here",
            "**Playbook study matters** — Knowing what plays to call in different situations takes time and practice",
            "**Celebrate smart play-calling** — Good decisions matter as much as execution. A smart checkdown is better than a forced throw!",
            "**Practice mode helps** — Running plays in practice mode without opponent pressure builds muscle memory",
            "**Watch NFL together** — Real NFL strategies translate directly to Madden. Good learning opportunity!",
            "**Defense wins championships** — Just like real football, forcing turnovers and stops is often the difference",
            "**It's okay to have favorite plays** — Everyone develops 'go-to' plays. It's part of building a scheme.",
        ],
        enjoyingWatching: [
            "**Watch it like NFL football** — The scoreboard, possession, downs, and yards work exactly like the real thing",
            "**Play calling is where skill shows** — Noticing good audibles and hot routes is satisfying once you learn to spot them",
            "**Turnovers are game-changers** — Interceptions and fumbles create huge momentum swings",
            "**Time and clock matter** — Just like real football, clock management in the 4th quarter is crucial",
            "**The user-controlled player makes plays** — Watch for when your player takes control of a defender and makes a big play",
            "**Pre-snap adjustments** — Advanced players make lots of changes before the snap. That's high-level Madden!",
            "**Ask about the playcall** — After a big play, ask your player what play they called and why it worked",
        ],
    },
};

export default function ParentsGuideGamePage() {
    const { gameId } = useParams<{ gameId: string }>();
    const guide = gameId ? guides[gameId] : undefined;

    if (!guide) {
        return <Navigate to="/parents" replace />;
    }

    return (
        <div className="relative overflow-hidden">
            {/* Hero Section */}
            <section className="relative">
                {/* Hero Image */}
                <div className="h-48 md:h-64 overflow-hidden">
                    <img
                        src={guide.heroImage}
                        alt={guide.title}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-secondary-900 via-secondary-900/50 to-transparent" />
                </div>

                {/* Title Overlay */}
                <div className="absolute bottom-0 left-0 right-0 pb-6">
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                        <Link to="/parents" className="inline-flex items-center text-blue-400 hover:text-blue-300 text-sm mb-3 transition-colors">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                            Back to Parents Guide
                        </Link>
                        <h1 className="text-3xl md:text-4xl font-display font-extrabold text-white mb-2">
                            {guide.title}
                        </h1>
                        <div className="flex flex-wrap items-center gap-3 text-sm">
                            <span className="badge bg-secondary-700 text-secondary-300">{guide.platform}</span>
                            <span className="badge bg-green-500/20 text-green-400">{guide.esrb}</span>
                            <span className="text-secondary-400">{guide.esrbDescription}</span>
                        </div>
                    </div>
                </div>
            </section>

            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Quick Navigation */}
                <nav className="mb-8 overflow-x-auto">
                    <div className="flex gap-2 min-w-max">
                        <a href="#what-is-this" className="badge bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors">🎮 What Is This?</a>
                        <a href="#videos" className="badge bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors">📺 Videos</a>
                        <a href="#rules" className="badge bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors">📋 Rules</a>
                        <a href="#scoring" className="badge bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors">🏆 Scoring</a>
                        <a href="#what-to-watch" className="badge bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors">👀 What to Watch</a>
                        <a href="#support" className="badge bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors">💪 Support Tips</a>
                        <a href="#enjoy" className="badge bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors">🎉 Enjoying Watching</a>
                    </div>
                </nav>

                <div className="space-y-12">
                    {/* What Is This Game */}
                    <section id="what-is-this" className="scroll-mt-24">
                        <h2 className="text-2xl font-display font-bold text-white mb-4 flex items-center gap-2">
                            <span className="text-3xl">🎮</span> What Is This Game?
                        </h2>
                        <div className="card">
                            <div className="space-y-4 text-secondary-300">
                                {guide.whatIsThisGame.map((para, idx) => (
                                    <p key={idx}>{para}</p>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* Explainer Videos */}
                    {guide.explainerVideos.length > 0 && (
                        <section id="videos" className="scroll-mt-24">
                            <h2 className="text-2xl font-display font-bold text-white mb-4 flex items-center gap-2">
                                <span className="text-3xl">📺</span> Explainer Videos
                            </h2>
                            <div className="grid md:grid-cols-2 gap-6">
                                {guide.explainerVideos.map((video, idx) => (
                                    <div key={idx} className="card">
                                        <div className="aspect-video mb-4 rounded-lg overflow-hidden bg-secondary-800">
                                            <iframe
                                                src={video.embedUrl}
                                                title={video.title}
                                                className="w-full h-full"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                            />
                                        </div>
                                        <h3 className="font-semibold text-white mb-1">{video.title}</h3>
                                        <p className="text-secondary-400 text-sm">{video.description}</p>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Rules */}
                    <section id="rules" className="scroll-mt-24">
                        <h2 className="text-2xl font-display font-bold text-white mb-4 flex items-center gap-2">
                            <span className="text-3xl">📋</span> The Rules
                        </h2>
                        <div className="card">
                            <div className="grid md:grid-cols-2 gap-6 mb-6">
                                <div>
                                    <h3 className="text-sm font-semibold text-secondary-400 mb-1">Format</h3>
                                    <p className="text-white text-lg">{guide.rules.format}</p>
                                </div>
                                <div>
                                    <h3 className="text-sm font-semibold text-secondary-400 mb-1">Match Length</h3>
                                    <p className="text-white text-lg">{guide.rules.matchLength}</p>
                                </div>
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-secondary-400 mb-2">How to Win</h3>
                                <ol className="space-y-2">
                                    {guide.rules.howToWin.map((step, idx) => (
                                        <li key={idx} className="flex items-start gap-3 text-secondary-300">
                                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-500/20 text-primary-400 flex items-center justify-center text-sm font-semibold">
                                                {idx + 1}
                                            </span>
                                            {step}
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        </div>
                    </section>

                    {/* Scoring */}
                    <section id="scoring" className="scroll-mt-24">
                        <h2 className="text-2xl font-display font-bold text-white mb-4 flex items-center gap-2">
                            <span className="text-3xl">🏆</span> Scoring & Winning
                        </h2>
                        <div className="card">
                            <div className="space-y-4 text-secondary-300 mb-6">
                                {guide.scoring.explanation.map((para, idx) => (
                                    <p key={idx}>{para}</p>
                                ))}
                            </div>

                            {guide.scoring.keyTerms.length > 0 && (
                                <div>
                                    <h3 className="text-sm font-semibold text-secondary-400 mb-3">Key Terms</h3>
                                    <div className="grid md:grid-cols-2 gap-3">
                                        {guide.scoring.keyTerms.map((term, idx) => (
                                            <div key={idx} className="bg-secondary-800 rounded-lg p-3 border border-secondary-700">
                                                <span className="font-semibold text-primary-400">{term.term}</span>
                                                <span className="text-secondary-400 ml-2">— {term.definition}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* What to Watch For */}
                    <section id="what-to-watch" className="scroll-mt-24">
                        <h2 className="text-2xl font-display font-bold text-white mb-4 flex items-center gap-2">
                            <span className="text-3xl">👀</span> What to Watch For
                        </h2>

                        <div className="grid md:grid-cols-2 gap-6">
                            {/* Exciting Moments */}
                            <div className="card">
                                <h3 className="font-semibold text-white mb-4">Exciting Moments</h3>
                                <ul className="space-y-3">
                                    {guide.whatToWatch.excitingMoments.map((moment, idx) => (
                                        <li key={idx} className="text-secondary-300 text-sm" dangerouslySetInnerHTML={{
                                            __html: moment.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>')
                                        }} />
                                    ))}
                                </ul>
                            </div>

                            {/* Glossary */}
                            <div className="card">
                                <h3 className="font-semibold text-white mb-4">Glossary</h3>
                                <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
                                    {guide.whatToWatch.glossary.map((term, idx) => (
                                        <div key={idx} className="text-sm">
                                            <span className="font-semibold text-blue-400">{term.term}</span>
                                            <p className="text-secondary-400 mt-0.5">{term.definition}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Supporting Your Player */}
                    <section id="support" className="scroll-mt-24">
                        <h2 className="text-2xl font-display font-bold text-white mb-4 flex items-center gap-2">
                            <span className="text-3xl">💪</span> How to Support Your Player
                        </h2>
                        <div className="card border-green-500/30 bg-gradient-to-br from-green-500/5 to-transparent">
                            <ul className="space-y-4">
                                {guide.supportingYourPlayer.map((tip, idx) => (
                                    <li key={idx} className="flex items-start gap-3 text-secondary-300" dangerouslySetInnerHTML={{
                                        __html: `<span class="text-green-400">✓</span> ${tip.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>')}`
                                    }} />
                                ))}
                            </ul>
                        </div>
                    </section>

                    {/* Enjoying Watching */}
                    <section id="enjoy" className="scroll-mt-24">
                        <h2 className="text-2xl font-display font-bold text-white mb-4 flex items-center gap-2">
                            <span className="text-3xl">🎉</span> Tips for Enjoying Watching
                        </h2>
                        <div className="card border-purple-500/30 bg-gradient-to-br from-purple-500/5 to-transparent">
                            <ul className="space-y-4">
                                {guide.enjoyingWatching.map((tip, idx) => (
                                    <li key={idx} className="flex items-start gap-3 text-secondary-300" dangerouslySetInnerHTML={{
                                        __html: `<span class="text-purple-400">★</span> ${tip.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>')}`
                                    }} />
                                ))}
                            </ul>
                        </div>
                    </section>
                </div>

                {/* Navigation Footer */}
                <div className="mt-12 pt-8 border-t border-secondary-700">
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                        <Link to="/parents" className="btn-secondary">
                            ← All Game Guides
                        </Link>
                        <p className="text-secondary-500 text-sm">
                            Questions? Contact Coach Anklam at{' '}
                            <a href="mailto:anklamb@peckschools.net" className="text-primary-400 hover:underline">anklamb@peckschools.net</a>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
